import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Grid,
  Autocomplete,
  Rating,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DOMPurify from 'dompurify';
import { useCustomToast, ToastVariant } from '../../components/Methods/SnackBar';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { apiEndpoint } from '../../Service/Apis';
import Loading from '../../components/Loading/Loading';
import SkillDocumentCommentBox from '../../components/SkillDocumentCommentBox/SkillDocumentCommentBox';
import PageTitle from '../../components/Page Component/PageTitle';
import { AUTH_HEADER, richHtmlSx, STATUS_COLORS } from '../../utils/trainingConstants';
import { setIsLoading } from '../../store/LoadingSlice';
import { RootState } from '../../store/Store';
import { STANDARD_DATE_FORMAT } from '../../utils/dateUtils';

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  employee_code: string;
}

interface Training {
  id: string;
  training_name: string;
  template_sheet_url: string;
}

interface TrainingAssignment {
  id: string;
  employee_name: string;
  training_name: string;
  copied_sheet_url: string;
  start_date: string;
  end_date: string;
  status: string;
  is_overdue: boolean;
  review_manager_name: string;
  comment: string;
  hr_comment: string | null;
  cumulative_manager_score: number | null;
  has_feedback: boolean;
}

interface FeedbackQuestion {
  id: string;
  question: string;
  question_text: string;
  question_type: 'stars' | 'comment';
  question_order: number;
  order: number;
}

interface ManagerFeedback {
  id: string;
  comment: string;
  responses: Array<{
    id: string;
    question: string;
    question_text: string;
    question_type: string;
    question_order: number;
    rating?: number;
    comment?: string;
  }>;
  cumulative_score: number | null;
}
const getSheetErrorMessage = (detail: string): string => {
  if (detail?.includes('replicate') || detail?.includes('replication')) {
    return 'Please ensure that the Google Sheet is shareable with Yodaplus or Anyone and try again.';
  }
  return detail || 'Failed to assign training';
};

const TrainingDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const selectedRole: string = useSelector((state: any) => state.auth.selectedRole) || '';
  const isLoading = useSelector((state: RootState) => state.loading.isLoading);
  const isHrOrAdmin = ['hr', 'admin'].includes(selectedRole.toLowerCase());
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [feedbackQuestions, setFeedbackQuestions] = useState<FeedbackQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [openResponseDialog, setOpenResponseDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [commentDialog, setCommentDialog] = useState({ open: false, content: '', title: 'Comment' });

  const [selectedAssignment, setSelectedAssignment] = useState<string | null>(null);
  const [selectedDetailAssignment, setSelectedDetailAssignment] = useState<TrainingAssignment | null>(null);
  const [selectedCompleteAssignment, setSelectedCompleteAssignment] = useState<TrainingAssignment | null>(null);
  const [viewingFeedback, setViewingFeedback] = useState<ManagerFeedback | null>(null);

  const [assignForm, setAssignForm] = useState({
    employee: null as Employee | null,
    training: null as Training | null,
    start_date: dayjs() as Dayjs,
    end_date: dayjs().add(30, 'day') as Dayjs,
    review_manager: null as Employee | null,
    comment: '',
  });

  const [feedbackForm, setFeedbackForm] = useState({
    comment: '',
    responses: [] as { question: string; rating?: number; comment?: string }[],
  });

  const [hrComment, setHrComment] = useState('');

  const showToast = useCustomToast();

  useEffect(() => {
    fetchAssignments();
    fetchEmployees();
    fetchTrainings();
    fetchFeedbackQuestions();
  }, []);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiEndpoint}/skillset/training/assignments/`, {
        headers: AUTH_HEADER(),
        params: { context: 'manage', selected_role: selectedRole },
      });
      setAssignments(response.data);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch assignments', ToastVariant.ERROR, true);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${apiEndpoint}/employees/view-active-users/`, {
        headers: AUTH_HEADER(),
      });
      setEmployees(response.data);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch employees', ToastVariant.ERROR, true);
    }
  };

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${apiEndpoint}/skillset/training/master/`, {
        headers: AUTH_HEADER(),
      });
      setTrainings(response.data.filter((t: Training & { is_active: boolean }) => t.is_active));
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch trainings', ToastVariant.ERROR, true);
    }
  };

  const fetchFeedbackQuestions = async () => {
    try {
      const response = await axios.get(`${apiEndpoint}/skillset/training/feedback-questions/`, {
        headers: AUTH_HEADER(),
      });
      setFeedbackQuestions(
        response.data.filter((q: FeedbackQuestion & { is_active: boolean }) => q.is_active)
      );
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch feedback questions', ToastVariant.ERROR, true);
    }
  };

  const handleOpenAssignDialog = () => {
    setAssignForm({
      employee: null,
      training: null,
      start_date: dayjs(),
      end_date: dayjs().add(30, 'day'),
      review_manager: null,
      comment: '',
    });
    setOpenAssignDialog(true);
  };

  const handleAssignTraining = async () => {
    if (!assignForm.employee || !assignForm.training || !assignForm.review_manager) {
      showToast('Please fill all required fields', ToastVariant.ERROR, true);
      return;
    }

    try {
      dispatch(setIsLoading(true));
      await axios.post(
        `${apiEndpoint}/skillset/training/assignments/`,
        {
          employee: assignForm.employee.id,
          training: assignForm.training.id,
          start_date: assignForm.start_date.format('YYYY-MM-DD'),
          end_date: assignForm.end_date.format('YYYY-MM-DD'),
          review_manager: assignForm.review_manager.id,
          comment: assignForm.comment,
          status: 'assigned',
        },
        { headers: AUTH_HEADER() }
      );
      showToast('Training assigned successfully', ToastVariant.SUCCESS, true);
      fetchAssignments();
      setOpenAssignDialog(false);
    } catch (error: any) {
      const detail = error.response?.data?.detail ?? '';
      showToast(getSheetErrorMessage(detail), ToastVariant.ERROR, true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const getAvailableManagers = () => {
    if (!assignForm.employee) return employees;
    return employees.filter((e) => e.id !== assignForm.employee?.id);
  };

  const handleOpenFeedbackDialog = (assignmentId: string) => {
    setSelectedAssignment(assignmentId);
    setFeedbackForm({
      comment: '',
      responses: feedbackQuestions.map((q) => ({
        question: q.id,
        rating: q.question_type === 'stars' ? 0 : undefined,
        comment: q.question_type === 'comment' ? '' : undefined,
      })),
    });
    setOpenFeedbackDialog(true);
  };

  const handleSubmitFeedback = async () => {
    if (!selectedAssignment) return;

    const hasInvalidResponses = feedbackForm.responses.some((r) => {
      const question = feedbackQuestions.find((q) => q.id === r.question);
      if (!question) return false;
      if (question.question_type === 'stars' && (!r.rating || r.rating === 0)) return true;
      if (question.question_type === 'comment' && (!r.comment || r.comment.trim() === '')) return true;
      return false;
    });

    if (hasInvalidResponses) {
      showToast('Please complete all feedback fields', ToastVariant.ERROR, true);
      return;
    }

    if (!feedbackForm.comment || feedbackForm.comment.trim() === '') {
      showToast('Final comment is mandatory', ToastVariant.ERROR, true);
      return;
    }

    try {
      await axios.post(
        `${apiEndpoint}/skillset/training/assignments/${selectedAssignment}/feedback/`,
        {
          comment: feedbackForm.comment,
          is_submitted: true,
          responses: feedbackForm.responses,
        },
        { headers: AUTH_HEADER() }
      );
      showToast('Feedback submitted successfully', ToastVariant.SUCCESS, true);
      fetchAssignments();
      setOpenFeedbackDialog(false);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to submit feedback', ToastVariant.ERROR, true);
    }
  };

  const handleViewScore = async (assignmentId: string) => {
    try {
      const response = await axios.get(
        `${apiEndpoint}/skillset/training/assignments/${assignmentId}/feedback/`,
        { headers: AUTH_HEADER() }
      );
      setViewingFeedback(response.data);
      setOpenResponseDialog(true);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch feedback', ToastVariant.ERROR, true);
    }
  };


  const handleOpenCompleteDialog = (assignment: TrainingAssignment) => {
    setSelectedCompleteAssignment(assignment);
    setHrComment('');
    setOpenCompleteDialog(true);
  };

  const handleCompleteTraining = async () => {
    if (!selectedCompleteAssignment) return;
    if (!hrComment.trim()) {
      showToast('HR comment is mandatory to complete the training', ToastVariant.ERROR, true);
      return;
    }

    try {
      await axios.patch(
        `${apiEndpoint}/skillset/training/assignments/${selectedCompleteAssignment.id}/status/`,
        { status: 'completed', hr_comment: hrComment },
        { headers: AUTH_HEADER() }
      );
      showToast('Training marked as completed', ToastVariant.SUCCESS, true);
      fetchAssignments();
      setOpenCompleteDialog(false);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to complete training', ToastVariant.ERROR, true);
    }
  };
  const getStatusCount = (s: string) => assignments.filter((a) => a.status === s).length;

  const openCommentPopup = (content: string, title: string) => {
    setCommentDialog({ open: true, content, title });
  };

  if (loading) return <Loading />;
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
        <Box sx={{ mb: 3 }}>
          <PageTitle>Training Dashboard</PageTitle>
        </Box>

        {isHrOrAdmin && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAssignDialog}>
              Assign Training
            </Button>
          </Box>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {['assigned', 'accepted', 'in_progress', 'under_evaluation', 'evaluated', 'completed'].map((s) => (
            <Grid item xs={12} sm={6} md={2} key={s}>
              <Card sx={{ height: '100%' }}>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{getStatusCount(s)}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ textTransform: 'capitalize', mt: 1 }}>
                    {s.replace(/_/g, ' ')}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Assignments Table */}
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr. No.</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Training Name</TableCell>
                  <TableCell align="center">Sheet</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Manager</TableCell>
                  <TableCell align="center">Assignment Comment</TableCell>
                  <TableCell align="center">HR Comment</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment, index) => (
                  <TableRow key={assignment.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{assignment.employee_name}</TableCell>

                    {/* Training name — click to open detail popup */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                        onClick={() => {
                          setSelectedDetailAssignment(assignment);
                          setOpenDetailDialog(true);
                        }}
                      >
                        {assignment.training_name}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {assignment.copied_sheet_url && (
                        <Tooltip title="Open Google Sheet">
                          <IconButton size="small" onClick={() => window.open(assignment.copied_sheet_url, '_blank')}>
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{new Date(assignment.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(assignment.end_date).toLocaleDateString()}</TableCell>

                    {/* Status — show overdue badge when applicable */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          label={assignment.status.replace(/_/g, ' ')}
                          color={STATUS_COLORS[assignment.status]}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                        {assignment.is_overdue && (
                          <Chip label="Overdue" color="error" size="small" />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>{assignment.review_manager_name}</TableCell>

                    {/* Assignment comment (given at assign time) */}
                    <TableCell align="center">
                      {assignment.comment ? (
                        <Tooltip title="View Assignment Comment">
                          <IconButton size="small" onClick={() => openCommentPopup(assignment.comment, 'Assignment Comment')}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>

                    {/* HR comment (given when completing) */}
                    <TableCell align="center">
                      {assignment.hr_comment ? (
                        <Tooltip title="View HR Comment">
                          <IconButton size="small" onClick={() => openCommentPopup(assignment.hr_comment!, 'HR Comment')}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>

                    {/* Score — click to view full feedback details */}
                    <TableCell align="center">
                      {assignment.cumulative_manager_score !== null ? (
                        <Tooltip title="Click to view feedback details">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                            onClick={() => handleViewScore(assignment.id)}
                          >
                            {assignment.cumulative_manager_score.toFixed(1)} / 5
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">N/A</Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {assignment.status === 'under_evaluation' && !assignment.has_feedback && (
                          <Tooltip title="Give Feedback">
                            <IconButton size="small" color="primary" onClick={() => handleOpenFeedbackDialog(assignment.id)}>
                              <FeedbackIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {assignment.status === 'evaluated' && (isHrOrAdmin) && (
                          <Tooltip title="Complete Training">
                            <IconButton size="small" color="success" onClick={() => handleOpenCompleteDialog(assignment)}>
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Comment viewer dialog (reused for assignment comment & HR comment) */}
        <Dialog open={commentDialog.open} onClose={() => setCommentDialog({ open: false, content: '', title: '' })} maxWidth="md" fullWidth>
          <DialogTitle>{commentDialog.title}</DialogTitle>
          <DialogContent>
            <Box
              sx={{ ...richHtmlSx, mt: 1, maxHeight: '65vh' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(commentDialog.content) }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCommentDialog({ open: false, content: '', title: '' })}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={openDetailDialog} onClose={() => setOpenDetailDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Training Details</DialogTitle>
          <DialogContent>
            {selectedDetailAssignment && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Training Name</Typography>
                  <Typography variant="body1">{selectedDetailAssignment.training_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                  <Typography variant="body1">{selectedDetailAssignment.employee_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={selectedDetailAssignment.status.replace(/_/g, ' ')}
                      color={STATUS_COLORS[selectedDetailAssignment.status]}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                    {selectedDetailAssignment.is_overdue && <Chip label="Overdue" color="error" size="small" />}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Duration</Typography>
                  <Typography variant="body1">
                    {new Date(selectedDetailAssignment.start_date).toLocaleDateString()} –{' '}
                    {new Date(selectedDetailAssignment.end_date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Review Manager</Typography>
                  <Typography variant="body1">{selectedDetailAssignment.review_manager_name}</Typography>
                </Box>
                {selectedDetailAssignment.comment && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Assignment Comment</Typography>
                    <Box
                      sx={{ ...richHtmlSx, maxHeight: '250px' }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedDetailAssignment.comment) }}
                    />
                  </Box>
                )}
                {selectedDetailAssignment.hr_comment && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>HR Comment</Typography>
                    <Box
                      sx={{ ...richHtmlSx, maxHeight: '250px' }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedDetailAssignment.hr_comment!) }}
                    />
                  </Box>
                )}
                {selectedDetailAssignment.copied_sheet_url && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<LinkIcon />}
                      onClick={() => window.open(selectedDetailAssignment.copied_sheet_url, '_blank')}
                    >
                      Open Training Sheet
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetailDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Assign Training</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.employee_code})`}
                value={assignForm.employee}
                onChange={(_, newValue) => setAssignForm({ ...assignForm, employee: newValue })}
                renderInput={(params) => <TextField {...params} label="Select Employee *" />}
              />
              <Autocomplete
                options={trainings}
                getOptionLabel={(option) => option.training_name}
                value={assignForm.training}
                onChange={(_, newValue) => setAssignForm({ ...assignForm, training: newValue })}
                renderInput={(params) => <TextField {...params} label="Training Name *" />}
              />
              <TextField
                label="Google Sheet URL"
                value={assignForm.training?.template_sheet_url || ''}
                InputProps={{ readOnly: true }}
                disabled
                helperText="Auto-filled when training is selected. A copy will be created for the employee."
              />
              <DatePicker
                label="Start Date *"
                value={assignForm.start_date}
                onChange={(v) => v && setAssignForm({ ...assignForm, start_date: v })}
                format={STANDARD_DATE_FORMAT}
              />
              <DatePicker
                label="End Date *"
                value={assignForm.end_date}
                onChange={(v) => v && setAssignForm({ ...assignForm, end_date: v })}
                format={STANDARD_DATE_FORMAT}
              />
              <Autocomplete
                options={getAvailableManagers()}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.employee_code})`}
                value={assignForm.review_manager}
                onChange={(_, newValue) => setAssignForm({ ...assignForm, review_manager: newValue })}
                renderInput={(params) => <TextField {...params} label="Review Manager *" />}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Assignment Comment (Optional)</Typography>
                <SkillDocumentCommentBox
                  value={assignForm.comment}
                  onChange={(value) => setAssignForm({ ...assignForm, comment: value })}
                  placeholder="Add any additional comments or instructions..."
                  compact
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignTraining} variant="contained" disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Assign Training'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openFeedbackDialog} onClose={() => setOpenFeedbackDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Manager Feedback</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {feedbackQuestions.map((question, index) => (
                <Box key={question.id}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    {index + 1}. {question.question}
                  </Typography>
                  {question.question_type === 'stars' ? (
                    <Box>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                        1 = Poor, 5 = Excellent
                      </Typography>
                      <Rating
                        value={feedbackForm.responses.find((r) => r.question === question.id)?.rating || 0}
                        onChange={(_, newValue) => {
                          const updated = feedbackForm.responses.map((r) =>
                            r.question === question.id ? { ...r, rating: newValue || 0 } : r
                          );
                          setFeedbackForm({ ...feedbackForm, responses: updated });
                        }}
                        size="large"
                      />
                    </Box>
                  ) : (
                    <SkillDocumentCommentBox
                      value={feedbackForm.responses.find((r) => r.question === question.id)?.comment || ''}
                      onChange={(value) => {
                        const updated = feedbackForm.responses.map((r) =>
                          r.question === question.id ? { ...r, comment: value } : r
                        );
                        setFeedbackForm({ ...feedbackForm, responses: updated });
                      }}
                      placeholder="Enter your response..."
                      compact
                    />
                  )}
                </Box>
              ))}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Final Comment <span style={{ color: 'red' }}>*</span>
                </Typography>
                <SkillDocumentCommentBox
                  value={feedbackForm.comment}
                  onChange={(value) => setFeedbackForm({ ...feedbackForm, comment: value })}
                  placeholder="Add your overall feedback comments (required)..."
                  compact
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFeedbackDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitFeedback} variant="contained">Submit Feedback</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openResponseDialog} onClose={() => setOpenResponseDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Feedback Score Details</DialogTitle>
          <DialogContent>
            {viewingFeedback && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                {[...viewingFeedback.responses]
                  .sort((a, b) => (a.question_order || 0) - (b.question_order || 0))
                  .map((response, index) => (
                    <Box key={response.id}>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                        {index + 1}. {response.question_text || response.question}
                      </Typography>
                      {response.question_type === 'stars' || response.rating ? (
                        <Rating value={response.rating || 0} readOnly size="large" />
                      ) : (
                        <Box
                          sx={{ ...richHtmlSx, maxHeight: '200px' }}
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(response.comment || 'No response provided') }}
                        />
                      )}
                    </Box>
                  ))}

                {viewingFeedback.cumulative_score != null && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Average Score (Star Ratings)</Typography>
                    <Typography variant="h5" color="primary">
                      {viewingFeedback.cumulative_score.toFixed(2)} / 5.0
                    </Typography>
                  </Box>
                )}

                {viewingFeedback.comment && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Final Comment</Typography>
                    <Box
                      sx={{ ...richHtmlSx, maxHeight: '250px' }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingFeedback.comment) }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenResponseDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openCompleteDialog} onClose={() => setOpenCompleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Complete Training</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                You are marking <strong>{selectedCompleteAssignment?.training_name}</strong> for{' '}
                <strong>{selectedCompleteAssignment?.employee_name}</strong> as completed.
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  HR Comment <span style={{ color: 'red' }}>*</span>
                </Typography>
                <SkillDocumentCommentBox
                  value={hrComment}
                  onChange={setHrComment}
                  placeholder="Enter your completion remarks (required)..."
                  compact
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCompleteDialog(false)}>Cancel</Button>
            <Button onClick={handleCompleteTraining} variant="contained" color="success">
              Mark as Completed
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default TrainingDashboard;
